/**
 * @file Same-tab printable report preview modal.
 */

import {
  showPrintError,
} from "./printWindow.js";

const PRINT_BUTTON_CLASS = [
  "standard-action-icon-btn hover:!text-blue-600",
  "focus-visible:!text-blue-600 dark:hover:!text-blue-400",
  "dark:focus-visible:!text-blue-400",
].join(" ");
const CLOSE_BUTTON_CLASS = [
  "standard-action-icon-btn standard-action-icon-btn--danger",
].join(" ");

function closePrintPreviewModal() {
  const modal = document.getElementById("print-preview-modal");
  if (!modal) {return;}
  const frame = modal.querySelector("#print-preview-frame");
  if (frame) {
    frame.removeAttribute("srcdoc");
    frame.src = "about:blank";
  }
  modal.classList.add("hidden");
  modal.classList.remove("flex");
}

function printPreviewFrame() {
  const frame = document.getElementById("print-preview-frame");
  try {
    frame?.contentWindow?.focus();
    frame?.contentWindow?.print();
  } catch (error) {
    console.error("Report preview print failed:", error);
    showPrintError("Could not print the report preview.");
  }
}

function getPrintPreviewModal() {
  let modal = document.getElementById("print-preview-modal");
  if (modal) {return modal;}

  modal = document.createElement("div");
  modal.id = "print-preview-modal";
  modal.className = [
    "fixed",
    "inset-0",
    "z-[70]",
    "hidden",
    "items-center",
    "justify-center",
    "bg-black/60",
    "p-3",
    "sm:p-6",
  ].join(" ");
  modal.innerHTML = `
    <div class="flex max-h-[92vh] w-full max-w-[880px] flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
      <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div class="min-w-0">
          <h3 id="print-preview-title" class="truncate text-base font-semibold text-gray-900 dark:text-gray-100"></h3>
          <p id="print-preview-subtitle" class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400"></p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button id="print-preview-print" type="button" class="${PRINT_BUTTON_CLASS}" title="Print" aria-label="Print">
            <span class="material-symbols-outlined text-[19px] leading-none">print</span>
          </button>
          <button id="print-preview-close" type="button" class="${CLOSE_BUTTON_CLASS}" title="Close preview" aria-label="Close preview">
            <span class="material-symbols-outlined text-[20px] leading-none">close</span>
          </button>
        </div>
      </div>
      <div class="min-h-[260px] flex-1 overflow-auto bg-gray-100 p-3 dark:bg-gray-900 sm:p-5">
        <iframe
          id="print-preview-frame"
          data-print-preview-frame
          title="Report preview"
          class="mx-auto block h-[78vh] w-full max-w-[794px] border border-gray-200 bg-white shadow-sm dark:border-gray-700"
        ></iframe>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  modal.querySelector("#print-preview-close")
    ?.addEventListener("click", closePrintPreviewModal);
  modal.querySelector("#print-preview-print")
    ?.addEventListener("click", printPreviewFrame);

  return modal;
}

/**
 * Opens a same-tab printable report preview.
 * @param {object} params Preview params.
 * @param {string} params.title Preview title.
 * @param {string} [params.subtitle] Preview subtitle.
 * @param {string} params.html Complete report HTML.
 * @returns {void}
 */
export function openPrintPreviewModal({
  title,
  subtitle = "",
  html,
}) {
  const modal = getPrintPreviewModal();
  modal.dataset.printPreviewModal = "true";
  modal.querySelector("#print-preview-title").textContent = title || "Report";
  modal.querySelector("#print-preview-subtitle").textContent = subtitle;

  const frame = modal.querySelector("#print-preview-frame");
  frame.title = title || "Report preview";
  frame.srcdoc = html || "";

  modal.classList.remove("hidden");
  modal.classList.add("flex");
}
